import { cn } from '../../lib/cn.ts';
import { Avatar, avatarInitials } from '../avatar/avatar.tsx';

export interface MemoryCirclesPerson {
  id: string;
  name: string;
  /** What the colour is hashed from — the address, so the circle matches their avatar. */
  seed?: string;
  /** Their picture, when there is one. */
  src?: string | null;
  /** Wundo already remembers something of the conversations with them. */
  memory: boolean;
}

/** The two halves of a large group, as the stacks split them. */
export type MemoryCirclesGroup = 'memory' | 'without';

export interface MemoryCirclesProps {
  people: readonly MemoryCirclesPerson[];
  /** Says what the circles show, for whoever cannot see them. */
  label: string;
  /**
   * What pressing a stack means, one per group — "Toon de collega's met
   * geheugen". Only read from nine people on, when there are stacks to press.
   */
  groupLabels?: Record<MemoryCirclesGroup, string>;
  /** Called with the stack that was pressed. Without it the stacks are a picture. */
  onPickGroup?: (group: MemoryCirclesGroup) => void;
  className?: string;
}

/* Up to this many people each get a circle; more and they become two stacks. */
const SEPARATE_UP_TO = 8;

/* How many circles a stack shows before the rest becomes "+N". */
const STACK_SHOWS = 5;

/**
 * One circle per person, the ones with a memory first: in full with a green
 * ring when Wundo has a memory of them, else an outline. From nine people on,
 * two stacks, with and without a memory, of five plus "+N", each opening the
 * list on that half.
 */
export function MemoryCircles({
  people,
  label,
  groupLabels,
  onPickGroup,
  className,
}: MemoryCirclesProps) {
  const withMemory = people.filter((person) => person.memory);
  const without = people.filter((person) => !person.memory);

  if (people.length > SEPARATE_UP_TO) {
    return (
      <div
        data-slot='memory-circles'
        role='group'
        aria-label={label}
        className={cn('flex flex-wrap items-center gap-x-8 gap-y-4', className)}
      >
        <Stack
          people={withMemory}
          group='memory'
          label={groupLabels?.memory}
          onPick={onPickGroup}
        />
        <Stack
          people={without}
          group='without'
          label={groupLabels?.without}
          onPick={onPickGroup}
        />
      </div>
    );
  }

  return (
    <div
      data-slot='memory-circles'
      role='img'
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-3.5', className)}
    >
      {[...withMemory, ...without].map((person) => (
        <MemoryCircle key={person.id} person={person} />
      ))}
    </div>
  );
}

/**
 * The same picture with nobody in it yet: three outlines where the circles
 * will be. Decoration for an empty page, so it says nothing to a screen reader.
 */
export function MemoryCirclesEmpty({ className }: { className?: string }) {
  return (
    <div
      data-slot='memory-circles-empty'
      aria-hidden='true'
      className={cn('flex items-center gap-3.5', className)}
    >
      {[0, 1, 2].map((index) => (
        <span key={index} className={cn(OUTLINE, 'size-11.5')} />
      ))}
    </div>
  );
}

interface StackProps {
  people: readonly MemoryCirclesPerson[];
  group: MemoryCirclesGroup;
  label?: string;
  onPick?: (group: MemoryCirclesGroup) => void;
}

/*
 * Five circles laid over each other and the rest counted. A button when there
 * is somewhere to go, a plain span when the picture is all there is — the same
 * markup either way, so the two never drift apart.
 */
function Stack({ people, group, label, onPick }: StackProps) {
  if (people.length === 0) return null;

  const rest = people.length - STACK_SHOWS;
  const circles = (
    <>
      {people.slice(0, STACK_SHOWS).map((person) => (
        <MemoryCircle key={person.id} person={person} className='not-first:-ml-2' />
      ))}
      {rest > 0 && (
        <span
          aria-hidden='true'
          className='-ml-2 flex size-11.5 items-center justify-center rounded-full bg-fill text-sm font-bold text-ink ring-2 ring-surface'
        >
          +{rest}
        </span>
      )}
    </>
  );
  const classes = 'flex items-center rounded-full';

  if (!onPick) {
    return (
      <span data-slot='memory-circles-stack' data-group={group} className={classes}>
        {circles}
      </span>
    );
  }

  return (
    <button
      type='button'
      data-slot='memory-circles-stack'
      data-group={group}
      onClick={() => onPick(group)}
      className={cn(
        classes,
        'cursor-pointer transition hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink-faint',
      )}
    >
      {/* Named by its content, not aria-label: the initials and "+N" on screen are a picture, not a label to repeat. */}
      <span className='sr-only'>{label}</span>
      {circles}
    </button>
  );
}

const OUTLINE =
  'flex shrink-0 items-center justify-center rounded-full border-2 border-dashed border-ink-faint bg-surface font-bold text-ink-muted';

export interface MemoryCircleProps {
  person: MemoryCirclesPerson;
  /** `lg` for the person a page is about; the picture on top uses the two smaller ones. */
  size?: 'sm' | 'md' | 'lg';
  /** Show their picture also without a memory, inside the dashed ring — for the one person a page is about. */
  showPicture?: boolean;
  className?: string;
}

const OUTLINE_SIZE = {
  sm: 'size-8 text-xs',
  md: 'size-11.5 text-md',
  lg: 'size-16 text-xl',
} as const;

/** One person's circle on its own — a header, drawn the way the picture on top draws them. */
export function MemoryCircle({
  person,
  size = 'md',
  showPicture = false,
  className,
}: MemoryCircleProps) {
  if (person.memory || (showPicture && person.src)) {
    return (
      <Avatar
        data-slot='memory-circle'
        data-memory={person.memory ? 'true' : 'false'}
        title={person.name}
        name={person.name}
        seed={person.seed}
        src={person.src}
        size={size}
        className={cn(
          person.memory
            ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface'
            : 'outline-2 outline-offset-2 outline-dashed outline-ink-faint',
          className,
        )}
      />
    );
  }

  /* Hidden like Avatar: the initials stand for a name said beside them or in the label. */
  return (
    <span
      data-slot='memory-circle'
      data-memory='false'
      aria-hidden='true'
      title={person.name}
      className={cn(OUTLINE, OUTLINE_SIZE[size], 'ring-2 ring-surface', className)}
    >
      {avatarInitials(person.name)}
    </span>
  );
}
